import { useEffect, useRef, useState } from 'react';
import UserService from '../services/User.service';
import { useAppDispatch } from '../store/hooks';
import { fetchPayments } from '../store/reducers/paymentsReducer';
import type { Payment } from '../types/user.types';

interface UsePaymentPollingProps {
  onSuccess: (payment: Payment) => void;
  onError: (errorMessage: string) => void;
  interval?: number;
  paymentId: number | null;
  enabled: boolean;
}

const POLLING_TIMEOUT = 3 * 60 * 1000; // 3 minutes

export function usePaymentPolling({ onSuccess, onError, interval = 4000, paymentId, enabled }: UsePaymentPollingProps) {
  const [isPolling, setIsPolling] = useState<boolean>(true);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const cleanupTimers = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    if (!enabled || !isPolling || !paymentId) {
      cleanupTimers();

      return;
    }

    const poll = async () => {
      try {
        const res = await UserService.getPayments();
        if (res.error) {
          throw new Error(res.message || 'Zahlungsstatus konnte nicht abgerufen werden.');
        }

        const allPayments: Payment[] = res.data || [];
        const targetPayment = allPayments.find((p) => p.id === paymentId);
        const isPaymentStillPending =
          !targetPayment || targetPayment.paymentStatus === 'started' || targetPayment.paymentStatus === 'unpaid';

        if (!isPaymentStillPending && targetPayment) {
          cleanupTimers();
          setIsPolling(false);
          dispatch(fetchPayments());
          onSuccess(targetPayment);
        }
      } catch (err: any) {
        console.error('Polling Error:', err);
        cleanupTimers();
        setIsPolling(false);
        onError(err.message || 'Bei der Überprüfung Ihrer Zahlung ist ein unerwarteter Fehler aufgetreten.');
      }
    };

    cleanupTimers();

    poll();
    pollingIntervalRef.current = setInterval(poll, interval);

    timeoutRef.current = setTimeout(() => {
      cleanupTimers();
      setIsPolling(false);
      onError('Die Zahlungsüberprüfung hat das Zeitlimit überschritten.');
    }, POLLING_TIMEOUT);

    return cleanupTimers;
  }, [isPolling, interval, onSuccess, onError, paymentId, enabled, dispatch]);
}
